/**
 * H5PPlayer
 * H5P content player with support for direct content or fetch by ID
 */

import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    H5PPlayerUI,
    hasH5pLearnerInput,
    readH5pScore,
    requestH5pGrade,
    type H5PPlayerContentData,
} from '@ai-content-hub/h5p-react';
import type { IPlayerModel } from '@ai-content-hub/h5p-server';
import {
    fetchContent,
    fetchH5PPlayerModel,
    extractH5PContentFromData,
    isApiConfigured,
} from '@ai-content-hub/shared-api';
import { resolveH5PParamsMediaUrls } from '@ai-content-hub/content-core';
import type { H5PContentItem } from '../../types/content';
import { routeXapiStatement } from './routeXapiStatement';
import { LoadingSpinner, LoadingOverlay } from '../common';

export interface BlankFocus {
    blank_index: number;
    filled_blanks: number[];
}

const BLANKS_LIBRARY_RE = /^H5P\.(Advanced)?Blanks(\s|-|$)/;
const BLANK_FIELD_SELECTOR =
    '.h5p-blanks input.h5p-text-input, .h5p-blanks select, .h5p-advanced-blanks input, .h5p-advanced-blanks select';
export const BLANK_FOCUS_THROTTLE_MS = 150;

export interface H5PPlayerProps {
    content?: H5PContentItem;
    contentId?: string;
    h5pLibrary?: string;
    readOnly?: boolean;
    onAnswer?: (answer: unknown) => void;
    /**
     * Fired on the xAPI `interacted` verb: the learner touched the activity (picked an
     * option, dropped a word, typed in a blank) WITHOUT a check/score statement. Check-less
     * hosts (LMS `allow_check` off hides the Check button) never get `answered`, so this is
     * their only "the learner has started answering" signal. Carries no score.
     */
    onInteract?: () => void;
    /**
     * AIH-18: Blanks / AdvancedBlanks only. Fired (throttled ~150 ms) when the learner
     * focuses or types in one of the blank fields. Indices are 1-based; `filled_blanks`
     * lists the blanks that currently hold something. Never carries the typed values.
     */
    onBlankFocus?: (focus: BlankFocus) => void;
    onSubmit?: (result: unknown) => void;
    onComplete?: (result: unknown) => void;
    onError?: (error: string) => void;
    onLoaded?: () => void;
    /**
     * Inline H5P `contentUserData` (e.g. from content-session snapshot / embed `qbUa`).
     * Forwarded to {@link H5PPlayerUI} `embedSessionContentUserData`.
     */
    embedSessionContentUserData?: unknown;
    /**
     * When true, keep hub `ajax.contentUserData` URLs from the player model (H5P REST server persistence).
     * Default false: strip hub routes; use {@link embedSessionContentUserData} or session APIs instead.
     */
    useH5pContentUserData?: boolean;
    /**
     * Session history: run Blanks post-check UI (score bar) after restore; pair with embed CSS
     * to hide Show solution / Try again.
     */
    historySubmittedPresentation?: boolean;
    /** ELMS-2370: LMS "hide answer" — see H5PPlayerUI `hideCorrectAnswers`. */
    hideCorrectAnswers?: boolean;
    /** Translated loading message. Defaults to 'Loading H5P content...'. */
    loadingMessage?: string;
    /**
     * Render this activity in its own iframe (EAA-1929). Pass it when the page hosts
     * SEVERAL H5P activities: div mode shares one window, one `H5PIntegration` and one
     * DOM id space across them, which leaked one activity's dropdown options into
     * another's blanks.
     */
    forceIframeEmbed?: boolean;
    /**
     * EAA-1029: H5P core's own chrome (Fullscreen, Check, Retry, Show solution, …) is
     * always in English until this is passed — the static bundle's manifest
     * ships both languages (see `apps/h5p-core/src/l10n-vi.json`), but nothing
     * picks one without a locale to pick with. Pass the app's active i18next
     * locale ('vi', 'vi-VN', 'en', …); this package stays i18n-agnostic on
     * purpose, so the CALLER reads it, not this component. Defaults to English.
     */
    language?: string;
}

export type H5PPlayerHandle = {
    getLearnerAnswerSnapshot: () => unknown | null;
    /**
     * Grade this activity NOW, as if the learner pressed "Check" (EAA-1929).
     *
     * The host calls this on a parent FORCE_SUBMIT. Without it an H5P question
     * submits with no score at all whenever the check button is hidden
     * (`hideSubmitButton`, i.e. LMS `allow_check` off) — the type only reports a
     * score from its own check handler.
     *
     * Returns true when a check path fired; the score then arrives through the
     * normal xAPI callback (`onAnswer` / `onComplete`). When it returns false the
     * caller should fall back to {@link H5PPlayerHandle.getScoreSnapshot}.
     */
    requestGrade: () => boolean;
    /** Current score read off the instance without grading it, or null when untouched. */
    getScoreSnapshot: () => { score: number; max: number } | null;
    /**
     * Did the learner put anything in? Ask this BEFORE
     * {@link H5PPlayerHandle.requestGrade}: grading marks the activity as answered, so
     * afterwards an untouched question is indistinguishable from an attempted one and
     * would be persisted with a real 0 instead of staying unscored.
     */
    hasLearnerInput: () => boolean;
};

export const H5PPlayer = forwardRef<H5PPlayerHandle, H5PPlayerProps>(function H5PPlayer(
    {
        content: contentProp,
        contentId: contentIdProp,
        h5pLibrary,
        readOnly = false,
        onAnswer,
        onInteract,
        onBlankFocus,
        onSubmit,
        onComplete,
        onError,
        onLoaded,
        embedSessionContentUserData,
        useH5pContentUserData = false,
        historySubmittedPresentation = false,
        hideCorrectAnswers = false,
        loadingMessage = 'Loading H5P content...',
        forceIframeEmbed = false,
        language,
    },
    ref,
) {
    const [content, setContent] = useState<H5PContentItem | null>(contentProp || null);
    const [isLoading, setIsLoading] = useState(false);
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const h5pPlayerRef = useRef<InstanceType<typeof H5PPlayerUI> | null>(null);

    useImperativeHandle(
        ref,
        () => ({
            getLearnerAnswerSnapshot: () => h5pPlayerRef.current?.getLearnerAnswerForSession() ?? null,
            // Delegated to H5PPlayerUI when available: it owns the ref to this
            // instance's own `<h5p-player>` element, and the DOM lookup MUST be scoped
            // to that element — H5P div mode puts every instance on the page in one
            // shared document, so a wider lookup would grade the first question
            // repeatedly.
            //
            // The `typeof` guards are not paranoia: h5p-react is a BUILT package
            // (`build/es`), and a consumer running an older build of it than this file
            // threw `getScoreSnapshot is not a function` mid-submit. Fall back to the
            // instance-only path, which needs no new API on that package.
            requestGrade: () => {
                const ui = h5pPlayerRef.current as
                    | (InstanceType<typeof H5PPlayerUI> & {
                          requestGrade?: () => boolean;
                      })
                    | null;
                if (typeof ui?.requestGrade === 'function') {
                    return ui.requestGrade();
                }
                if (typeof requestH5pGrade !== 'function') {
                    return false;
                }
                // No element scope on this path, so grading relies on the instance's own
                // check handler / `$container` (see requestH5pGrade's lookup order).
                return requestH5pGrade(ui?.h5pInstance ?? null, null);
            },
            hasLearnerInput: () => {
                const ui = h5pPlayerRef.current as
                    | (InstanceType<typeof H5PPlayerUI> & {
                          hasLearnerInput?: () => boolean;
                      })
                    | null;
                if (typeof ui?.hasLearnerInput === 'function') {
                    return ui.hasLearnerInput();
                }
                if (typeof hasH5pLearnerInput !== 'function') {
                    // Unknown on this build — treat as answered so a real attempt is
                    // never dropped; a false positive only costs an extra grade call.
                    return true;
                }
                return hasH5pLearnerInput(
                    ui?.h5pInstance ?? null,
                    ui?.getLearnerAnswerForSession?.() ?? null,
                );
            },
            getScoreSnapshot: () => {
                const ui = h5pPlayerRef.current as
                    | (InstanceType<typeof H5PPlayerUI> & {
                          getScoreSnapshot?: () => { score: number; max: number } | null;
                      })
                    | null;
                if (typeof ui?.getScoreSnapshot === 'function') {
                    return ui.getScoreSnapshot();
                }
                if (typeof readH5pScore !== 'function') {
                    return null;
                }
                return readH5pScore(ui?.h5pInstance ?? null);
            },
        }),
        [],
    );
    const contentId = content?.id || contentIdProp;

    useEffect(() => {
        // EAA-1857: H5P resolves every asset through H5P.getPath, which prepends the
        // content folder to anything without a scheme — so our root-relative
        // /media/... keys 404'd and images rendered broken. Swap them for the same
        // short-lived signed URLs ACH players use, before the player mounts.
        let cancelled = false;
        const withResolvedMedia = (item: H5PContentItem): Promise<H5PContentItem> =>
            resolveH5PParamsMediaUrls(item.h5p.params).then((params) => ({
                ...item,
                h5p: { ...item.h5p, params },
            }));

        if (contentProp) {
            withResolvedMedia(contentProp)
                .then((resolved) => {
                    if (!cancelled) setContent(resolved);
                })
                .catch(() => {
                    if (!cancelled) setContent(contentProp);
                });
            return () => {
                cancelled = true;
            };
        }

        if (contentIdProp && !contentProp) {
            setIsLoading(true);
            setError(null);

            fetchContent(contentIdProp)
                .then((fetchedContent) => {
                    const h5pData = extractH5PContentFromData(fetchedContent.data);
                    if (!h5pData) {
                        const errorMsg = 'This content does not contain H5P data. Use ACHPlayer instead.';
                        setError(errorMsg);
                        onError?.(errorMsg);
                        return;
                    }

                    const h5pContent: H5PContentItem = {
                        id: fetchedContent.id,
                        h5pLibrary: fetchedContent.content_type.library,
                        h5p: h5pData,
                    };

                    return withResolvedMedia(h5pContent).then((resolved) => {
                        if (cancelled) return;
                        setContent(resolved);
                        onLoaded?.();
                    });
                })
                .catch((err) => {
                    const errorMsg = err.message || 'Failed to load content';
                    setError(errorMsg);
                    onError?.(errorMsg);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }

        return () => {
            cancelled = true;
        };
    }, [contentIdProp, contentProp, onError, onLoaded]);

    const loadPlayerContentCallback = useCallback(async (): Promise<IPlayerModel> => {
        if (!isApiConfigured()) {
            const errorMsg = 'Shared API not configured. Call configureShared() first.';
            onError?.(errorMsg);
            throw new Error(errorMsg);
        }

        const library = h5pLibrary || content?.h5pLibrary || content?.h5p?.library;

        try {
            return await fetchH5PPlayerModel(library, language);
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Failed to load H5P player model';
            onError?.(errorMsg);
            throw new Error(errorMsg);
        }
    }, [h5pLibrary, content, onError, language]);

    const playerContentData: H5PPlayerContentData | undefined = useMemo(() => {
        if (!content?.h5p) {
            return undefined;
        }
        return {
            library: content.h5p.library,
            params: content.h5p.params,
            metadata: content.h5p.metadata,
        };
    }, [content]);

    const embedSessionRemountKey = useMemo(() => {
        if (embedSessionContentUserData === undefined || embedSessionContentUserData === null) {
            return 'none';
        }
        try {
            const s = JSON.stringify(embedSessionContentUserData ?? null);
            let h = 2166136261;
            for (let i = 0; i < s.length; i += 1) {
                h ^= s.charCodeAt(i);
                h = Math.imul(h, 16777619);
            }
            return `${(h >>> 0).toString(36)}-${s.length}`;
        } catch {
            return 'bad';
        }
    }, [embedSessionContentUserData]);

    const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (playerContentData) {
            setIsIframeLoaded(false);
            // Fallback: hide loading after 4s if H5P initialized event never fires
            loadTimeoutRef.current = setTimeout(() => {
                setIsIframeLoaded(true);
            }, 4000);
        }
        return () => {
            if (loadTimeoutRef.current) {
                clearTimeout(loadTimeoutRef.current);
                loadTimeoutRef.current = null;
            }
        };
    }, [playerContentData]);

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const onBlankFocusRef = useRef(onBlankFocus);
    onBlankFocusRef.current = onBlankFocus;
    const blankFocusCleanupRef = useRef<(() => void) | null>(null);
    const isBlanksContent = BLANKS_LIBRARY_RE.test(
        String(h5pLibrary || content?.h5pLibrary || content?.h5p?.library || ''),
    );

    // AIH-18: DOM focusin/input over the Blanks fields -> onBlankFocus. Div mode: scope to
    // this player's wrapper (one shared document for every instance); iframe mode: the
    // iframe's own document.
    const attachBlankFocus = useCallback(() => {
        blankFocusCleanupRef.current?.();
        blankFocusCleanupRef.current = null;
        if (!isBlanksContent || !onBlankFocusRef.current) return;
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        let boundRoot: Document | HTMLElement | null = null;
        let unbindRoot: (() => void) | null = null;
        let watchedIframe: HTMLIFrameElement | null = null;

        const bind = (target: Document | HTMLElement) => {
            let timer: ReturnType<typeof setTimeout> | null = null;
            let lastAt = 0;
            let pending: EventTarget | null = null;
            const report = (el: EventTarget | null) => {
                const list = Array.from(
                    target.querySelectorAll<HTMLInputElement | HTMLSelectElement>(BLANK_FIELD_SELECTOR),
                );
                const i = list.indexOf(el as HTMLInputElement);
                if (i < 0) return;
                onBlankFocusRef.current?.({
                    blank_index: i + 1,
                    // indices only, never the values
                    filled_blanks: list.flatMap((f, k) => (String(f.value ?? '').trim() ? [k + 1] : [])),
                });
            };
            const handler = (e: Event) => {
                pending = e.target;
                const wait = BLANK_FOCUS_THROTTLE_MS - (Date.now() - lastAt);
                if (wait <= 0) {
                    lastAt = Date.now();
                    const t = pending;
                    pending = null;
                    report(t);
                } else if (!timer) {
                    // trailing call so the last keystroke of a burst is not lost
                    timer = setTimeout(() => {
                        timer = null;
                        lastAt = Date.now();
                        const t = pending;
                        pending = null;
                        if (t) report(t);
                    }, wait);
                }
            };
            target.addEventListener('focusin', handler);
            target.addEventListener('input', handler);
            return () => {
                target.removeEventListener('focusin', handler);
                target.removeEventListener('input', handler);
                if (timer) clearTimeout(timer);
                timer = null;
                pending = null;
            };
        };

        // The web component can replace the iframe (renderIframe clears its root) or the
        // iframe document can reload: re-resolve the root and rebind, never twice on one root.
        const sync = () => {
            const iframe = wrapper.querySelector<HTMLIFrameElement>('iframe[id^="h5p-iframe-"]');
            if (iframe !== watchedIframe) {
                watchedIframe?.removeEventListener('load', sync);
                watchedIframe = iframe;
                iframe?.addEventListener('load', sync);
            }
            let next: Document | HTMLElement | null = wrapper;
            if (iframe) {
                try {
                    next = iframe.contentDocument;
                } catch {
                    next = null;
                }
            }
            if (next === boundRoot) return;
            unbindRoot?.();
            unbindRoot = null;
            boundRoot = next;
            if (next) unbindRoot = bind(next);
        };

        const observer =
            typeof MutationObserver !== 'undefined' ? new MutationObserver(sync) : null;
        observer?.observe(wrapper, { childList: true, subtree: true });
        sync();

        blankFocusCleanupRef.current = () => {
            observer?.disconnect();
            watchedIframe?.removeEventListener('load', sync);
            watchedIframe = null;
            unbindRoot?.();
            unbindRoot = null;
            boundRoot = null;
        };
    }, [isBlanksContent]);

    useEffect(
        () => () => {
            blankFocusCleanupRef.current?.();
            blankFocusCleanupRef.current = null;
        },
        [],
    );

    const onPlayerInitialized = useCallback(
        (initializedContentId: string) => {
            if (loadTimeoutRef.current) {
                clearTimeout(loadTimeoutRef.current);
                loadTimeoutRef.current = null;
            }
            setIsIframeLoaded(true);
            console.log('H5P player initialized:', initializedContentId);
            attachBlankFocus();
            onLoaded?.();
        },
        [onLoaded, attachBlankFocus]
    );

    const onxAPIStatement = useCallback(
        (statement: unknown, _context: unknown, _event: unknown) => {
            routeXapiStatement(statement, { onAnswer, onInteract, onSubmit, onComplete });
        },
        [onAnswer, onInteract, onSubmit, onComplete]
    );

    if (isLoading) {
        return (
            <div
                className="h5p-player-loading"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                    padding: 24,
                }}
            >
                <LoadingSpinner message={loadingMessage} />
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="h5p-player-error"
                style={{ padding: '24px', color: 'hsl(var(--destructive, 347 79% 46%))', textAlign: 'center' }}
            >
                {error}
            </div>
        );
    }

    const hasContent = playerContentData || contentId;

    if (!hasContent) {
        return (
            <div
                className="h5p-player-empty"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '48px 24px',
                    minHeight: 400,
                }}
            >
                <div>No content to display. Provide either content or contentId prop.</div>
            </div>
        );
    }

    const playerContentId = contentId || 'player';

    const h5pPlayerUiKey = `player-${playerContentId}-${embedSessionRemountKey}`;

    return (
        <div
            ref={wrapperRef}
            className="h5p-player"
            style={{
                position: 'relative',
                minHeight: 200,
                width: '100%',
                height: '100%',
            }}
        >
            <div style={{ padding: 12, height: '100%', minHeight: 200 }}>
                <H5PPlayerUI
                    key={h5pPlayerUiKey}
                    ref={h5pPlayerRef}
                    contentId={playerContentId}
                    contentData={playerContentData}
                    readOnlyState={readOnly}
                    loadContentCallback={loadPlayerContentCallback}
                    onInitialized={onPlayerInitialized}
                    onxAPIStatement={onxAPIStatement}
                    embedSessionContentUserData={embedSessionContentUserData}
                    useH5pContentUserData={useH5pContentUserData}
                    historySubmittedPresentation={historySubmittedPresentation}
                    hideCorrectAnswers={hideCorrectAnswers}
                    forceIframeEmbed={forceIframeEmbed}
                />
            </div>
            {!isIframeLoaded && (
                <LoadingOverlay
                    message={loadingMessage}
                />
            )}
        </div>
    );
});

export default H5PPlayer;
